import React, { useEffect, useState } from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, CircularProgress, Collapse, Tooltip
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { Alert } from '@material-ui/lab'
import { getNppesSavedRecords, deleteNppesSavedRecord } from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const formatDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const statusLabel = (code) => {
    if (code === 'A') return 'Active'
    if (code === 'D') return 'Deactivated'
    return code || '—'
}

const ExpandableSavedRow = ({ record, onDelete }) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>{record.matchedName || '—'}</TableCell>
                <TableCell>{record.matchedKey || '—'}</TableCell>
                <TableCell>{record.entityType || '—'}</TableCell>
                <TableCell>{record.credential || '—'}</TableCell>
                <TableCell>{record.primaryTaxonomyDesc || '—'}</TableCell>
                <TableCell>{record.primaryPracticeState || '—'}</TableCell>
                <TableCell>{statusLabel(record.providerStatus)}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    <Tooltip title='Remove saved match' arrow>
                        <IconButton
                            size='small'
                            onClick={(e) => { e.stopPropagation(); onDelete(record.id) }}
                            aria-label='remove'
                        >
                            <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div><strong>NPI:</strong> {record.matchedKey || '—'} &nbsp;&nbsp; <strong>Entity Type:</strong> {record.entityType || '—'}</div>
                            <div><strong>Status:</strong> {statusLabel(record.providerStatus)} &nbsp;&nbsp; <strong>Enumeration:</strong> {record.enumerationDate || '—'}</div>
                            <div><strong>Credential:</strong> {record.credential || '—'}</div>
                            <div><strong>Primary Taxonomy:</strong> {record.primaryTaxonomyDesc || '—'} {record.primaryTaxonomyCode ? `[${record.primaryTaxonomyCode}]` : ''}</div>
                            {record.primaryTaxonomyState && (
                                <div><strong>License State:</strong> {record.primaryTaxonomyState}</div>
                            )}
                            <div><strong>Practice State:</strong> {record.primaryPracticeState || '—'}</div>
                            <div><strong>Saved:</strong> {formatDate(record.createdAt)}</div>
                            {record.notes && (
                                <div style={{ marginTop: 6 }}><strong>Notes:</strong> <span style={{ wordBreak: 'break-word' }}>{record.notes}</span></div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const NppesSavedRecords = ({ applicantId, refreshKey, onRecordDeleted }) => {
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(false)

    const fetch = () => {
        setLoading(true)
        getNppesSavedRecords({ applicationId: applicantId })()
            .then(res => setRecords(Array.isArray(res) ? res : []))
            .catch(() => Toast({ type: 'error', message: 'Failed to load saved records' }))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (applicantId) fetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantId, refreshKey])

    const handleDelete = (recordId) => {
        deleteNppesSavedRecord({ applicationId: applicantId, recordId })()
            .then(() => {
                Toast({ type: 'success', message: 'Record removed' })
                fetch()
                // Notify parent so results-table bookmark icons refresh.
                if (typeof onRecordDeleted === 'function') onRecordDeleted()
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to remove record' }))
    }

    if (!applicantId) return null

    return (
        <div className='cardStyle mt-3'>
            <div className='uccTitleContainer'><h5>Saved NPI Registry Matches</h5></div>
            {loading ? (
                <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={20} /></div>
            ) : records.length === 0 ? (
                <Alert severity='info'>No NPI Registry matches saved to this application yet.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>NPI</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Credential</TableCell>
                                <TableCell>Primary Taxonomy</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map(r => (
                                <ExpandableSavedRow key={r.id} record={r} onDelete={handleDelete} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    )
}

export default NppesSavedRecords
